// The single source of truth for WHICH ROLES EACH METRIC NEEDS to compute.
// build-model derives each metric's data-quality requirements from this, and
// the mapping wizard derives its "metrics ready / blocked" coverage panel AND
// the fix-flow planner from the same table — so the wizard can never claim
// "all verified" while the cockpit shows a metric grey. One table, no drift.
import type { AccountRole, Metric } from '@/types/tracking-view';
import { ROLE_CLASSIFICATION } from './tie-out';

export interface MetricRequirement {
  id: string;
  /** Builder-facing name (trade language, matches the cockpit's labels). */
  label: string;
  /** Roles that MUST have data for the metric to compute (data-quality gate). */
  roles: AccountRole[];
}

export const METRIC_REQUIREMENTS: MetricRequirement[] = [
  // Health score consumes the margins (its profitability layer), so it gates on
  // the SAME COGS roles they do — without them, unmapped job costs read as $0
  // cost and the score sits near 100 with no flag (F-124 review #3).
  { id: 'health-score', label: 'Financial Health Score', roles: ['revenue', 'materials-cogs', 'subbie-cogs', 'direct-labour-cogs', 'other-direct-cost', 'overhead', 'cash-bucket'] },
  // Every COGS role totalCogs consumes — the gate and the arithmetic must not
  // drift (advisor #4: other-direct-cost was summed but ungated, so a chart
  // with money in it but no mapping got a confident overstated margin).
  { id: 'gross-margin', label: 'Gross margin', roles: ['revenue', 'materials-cogs', 'subbie-cogs', 'direct-labour-cogs', 'other-direct-cost'] },
  // Net margin and overhead recovery both consume totalCogs (via grossProfit) exactly like
  // gross-margin — so they must gate on the SAME COGS roles, or an unmapped COGS role yields a
  // confident overstated figure with no flag (the drift gross-margin fixed for itself alone).
  // The render layer shows these as a flagged approximate when a COGS role is missing; the gate
  // just has to STOP claiming they're clean.
  { id: 'net-margin', label: 'Net margin', roles: ['revenue', 'materials-cogs', 'subbie-cogs', 'direct-labour-cogs', 'other-direct-cost', 'overhead'] },
  { id: 'overhead-recovery', label: 'Overhead recovery', roles: ['revenue', 'materials-cogs', 'subbie-cogs', 'direct-labour-cogs', 'other-direct-cost', 'overhead'] },
  { id: 'cash-runway', label: 'Cash runway', roles: ['cash-bucket', 'overhead'] },
  { id: 'debtor-days', label: 'Debtor days', roles: ['revenue', 'trade-debtors'] },
  // The Gap (profit → cash hero): net profit needs revenue + overhead; the
  // working-capital movement needs all three WC balances — an unmapped
  // trade-creditors was silently reading as a $0 delta (found 2026-08-10).
  {
    id: 'the-gap',
    label: 'The Gap (profit → cash)',
    roles: ['revenue', 'overhead', 'trade-debtors', 'wip-asset', 'trade-creditors'],
  },
  // Matches build-model's scoreboardRequired ('revenue' + ASSET_ROLES) — was
  // drifted to just ['revenue'] (advisor #2), which let the wizard say "ready"
  // while the cockpit greyed the tiles.
  {
    id: 'scoreboard',
    label: 'The Scoreboard (4 tiles)',
    // overhead + trade-creditors feed the OCF inside productivity/cash-ROA —
    // without them gated, those tiles computed from silently-zeroed inputs.
    roles: [
      'revenue',
      'overhead',
      'cash-bucket',
      'tax-reserve-cash',
      'trade-debtors',
      // retentions-withheld is NOT required (Tim, 12 Oct): many builders never hold retentions, and
      // a missing one is a genuine $0 in total assets — the same rule The Gap already applies.
      'wip-asset',
      'other-current-asset',
      'ppe',
      'trade-creditors',
    ],
  },
];

/** Trade-language names for roles, used when telling a builder what's missing. */
export const ROLE_LABELS: Partial<Record<AccountRole, string>> = {
  'revenue': 'income',
  'materials-cogs': 'materials (job costs)',
  'subbie-cogs': 'subcontractors (job costs)',
  'direct-labour-cogs': 'direct site labour (job costs)',
  'overhead': 'running costs',
  // No leading articles — these get joined into comma lists, where "a"/"an"
  // only fits the first item ("a tax set-aside, retentions withheld and …").
  'cash-bucket': 'everyday bank account',
  'tax-reserve-cash': 'tax set-aside account',
  'trade-debtors': 'money owed to you (receivables)',
  'trade-creditors': 'money you owe (payables)',
  'wip-asset': 'work in progress',
  'retentions-withheld': 'retentions withheld',
  'other-current-asset': 'other current assets',
  'ppe': 'plant & equipment',
};

/** Compact variants for the checklist line (advisor #13 — the long labels triple on mobile). */
export const ROLE_LABELS_SHORT: Partial<Record<AccountRole, string>> = {
  'revenue': 'income',
  'materials-cogs': 'materials',
  'subbie-cogs': 'subbies',
  'direct-labour-cogs': 'site labour',
  'overhead': 'running costs',
  'cash-bucket': 'bank account',
  'tax-reserve-cash': 'tax set-aside',
  'trade-debtors': 'receivables',
  'trade-creditors': 'payables',
  'wip-asset': 'WIP',
  'retentions-withheld': 'retentions',
  'other-current-asset': 'other assets',
  'ppe': 'plant & equipment',
};

export const roleLabel = (r: AccountRole): string => ROLE_LABELS[r] ?? r;
export const roleLabelShort = (r: AccountRole): string => ROLE_LABELS_SHORT[r] ?? r;

export interface BlockedMetric {
  id: string;
  label: string;
  /** The missing roles blocking it, in trade language. */
  missing: string[];
  /** The same roles as codes — the fixer flow needs them to find candidates. */
  missingRoles: AccountRole[];
  /** EVERY role the metric needs — the checklist renders all of them. */
  requiredRoles: AccountRole[];
}

/**
 * Which metrics can't compute given the roles with no mapped account.
 * Feed it `coverage.missing` from classify (chart-level truth).
 */
export function blockedMetrics(missingRoles: AccountRole[]): BlockedMetric[] {
  const missing = new Set(missingRoles);
  return METRIC_REQUIREMENTS
    .map((req) => {
      const roles = req.roles.filter((r) => missing.has(r));
      return {
        id: req.id,
        label: req.label,
        missing: roles.map(roleLabel),
        missingRoles: roles,
        requiredRoles: req.roles,
      };
    })
    .filter((m) => m.missing.length > 0);
}

export function readyMetrics(missingRoles: AccountRole[]): MetricRequirement[] {
  const missing = new Set(missingRoles);
  return METRIC_REQUIREMENTS.filter((req) => !req.roles.some((r) => missing.has(r)));
}

/* -------------------------------------------------------------------------- */
/* The fix planner — advisor-hardened (blocks #1 #3 #4 #7 #12 + plausibility)  */
/* -------------------------------------------------------------------------- */

/** Minimal chart-row shape the planner needs (matches api XeroChartRow). */
export interface FixRow {
  accountId: string;
  role: string | null;
  /** Band-locked roles this account may take — the tie-out-safe boundary. */
  candidateRoles: Array<{ role: string; label: string }>;
  /**
   * Type-plausible subset (server-computed). Absent on old payloads or stale
   * caches -> fall back to the full band, per-row (advisor #9).
   */
  plausibleRoles?: string[];
  /** 'conflict' rows are never offered as chips — their type is in doubt. */
  consistency?: string;
}

/** The roles this row may be OFFERED for: plausible when known, band otherwise. */
function offersOf(row: FixRow): string[] {
  return row.plausibleRoles ?? row.candidateRoles.map((c) => c.role);
}

export interface RoleFixOptions<T> {
  role: AccountRole;
  /** Candidates whose move breaks nothing: unassigned, or their current role keeps another holder. */
  free: T[];
  /** Sole holders — taking one empties `vacates`. Shown with that consequence. */
  swap: Array<{ row: T; vacates: AccountRole }>;
}

/** A missing role that re-mapping cannot fill, with the why for the sentence. */
export interface UnfilledRole {
  role: AccountRole;
  /** How many (non-conflict) accounts could plausibly BE this role at all. */
  plausibleCount: number;
}

export interface MetricFixPlan<T> {
  perRole: Array<RoleFixOptions<T>>;
  /** True when re-mapping alone can cover every missing role simultaneously. */
  canUnlockByMapping: boolean;
  /** The missing roles the flow could NOT cover — drives the per-role why. */
  unfilled: UnfilledRole[];
  /** The sole holder to NAME in the split guide when mapping can't win (advisor #12). */
  primarySwap: { row: T; vacates: AccountRole } | null;
  /** Conflict-flagged plausible candidates — "check their type first", never chips (advisor #7). */
  checkFirst: T[];
}

/**
 * Exact unlockability via a small max-flow (plausibility broke the per-band
 * headcount: eligibility is no longer uniform inside a band, so Hall's
 * condition doesn't collapse to counting — two spare CURRENT accounts cannot
 * cover a BANK-only role, however the band count reads).
 *
 * Graph: S -> group (spare capacity) -> row (1) -> missing role (1) -> T.
 * A held role's group has capacity nonConflictHolders-1 (one stays behind —
 * WHICH one is the flow's choice, so mixed-type groups are handled exactly);
 * each unassigned row is its own group of capacity 1. Conflict rows get no
 * edges and donate no capacity (advisor #6a). Known accepted blindness
 * (advisor #6c): backfill chains (move sole holder A to the missing role,
 * refill A's role from an unassigned row) read as not-unlockable; the swap
 * chips still surface that move manually.
 */
function flowCover(missingRoles: AccountRole[], rows: FixRow[]): Set<AccountRole> {
  const holders = new Map<string, number>();
  for (const r of rows) {
    if (r.role && r.consistency !== 'conflict') {
      holders.set(r.role, (holders.get(r.role) ?? 0) + 1);
    }
  }
  const usable = rows.filter((r) =>
    r.consistency !== 'conflict' &&
    (r.role === null || (holders.get(r.role) ?? 0) >= 2) &&
    offersOf(r).some((o) => (missingRoles as string[]).includes(o))
  );
  if (usable.length === 0 || missingRoles.length === 0) return new Set();

  // Node ids: 0 = S, 1 = T, then groups, rows, roles.
  const groupIds = new Map<string, number>();
  let next = 2;
  const cap = new Map<number, Map<number, number>>();
  const addEdge = (a: number, b: number, c: number) => {
    if (!cap.has(a)) cap.set(a, new Map());
    if (!cap.has(b)) cap.set(b, new Map());
    cap.get(a)!.set(b, (cap.get(a)!.get(b) ?? 0) + c);
    if (!cap.get(b)!.has(a)) cap.get(b)!.set(a, 0);
  };
  for (const r of usable) {
    const g = r.role ?? `__row_${r.accountId}`;
    if (!groupIds.has(g)) {
      const id = next++;
      groupIds.set(g, id);
      addEdge(0, id, r.role ? (holders.get(r.role) ?? 1) - 1 : 1);
    }
  }
  const rowIds = new Map(usable.map((r) => [r.accountId, next++] as const));
  const roleIds = new Map(missingRoles.map((m) => [m, next++] as const));
  for (const r of usable) {
    addEdge(groupIds.get(r.role ?? `__row_${r.accountId}`)!, rowIds.get(r.accountId)!, 1);
    for (const m of missingRoles) {
      if (offersOf(r).includes(m)) addEdge(rowIds.get(r.accountId)!, roleIds.get(m)!, 1);
    }
  }
  for (const m of missingRoles) addEdge(roleIds.get(m)!, 1, 1);

  // Edmonds-Karp (BFS augmenting paths); tiny graph, unit-ish capacities.
  for (;;) {
    const parent = new Map<number, number>();
    const queue = [0];
    parent.set(0, -1);
    while (queue.length && !parent.has(1)) {
      const u = queue.shift()!;
      for (const [v, c] of cap.get(u) ?? []) {
        if (c > 0 && !parent.has(v)) {
          parent.set(v, u);
          queue.push(v);
        }
      }
    }
    if (!parent.has(1)) break;
    // Min residual along the path is 1 here (unit bottlenecks) — walk & apply.
    let v = 1;
    while (v !== 0) {
      const u = parent.get(v)!;
      cap.get(u)!.set(v, cap.get(u)!.get(v)! - 1);
      cap.get(v)!.set(u, (cap.get(v)!.get(u) ?? 0) + 1);
      v = u;
    }
  }
  // A missing role is covered iff its edge to T is saturated.
  const covered = new Set<AccountRole>();
  for (const m of missingRoles) {
    if ((cap.get(roleIds.get(m)!)?.get(1) ?? 1) === 0) covered.add(m);
  }
  return covered;
}

/**
 * Plan how a metric's missing roles could be filled from the (staged) chart.
 * Pass rows through `applyStagedAnswers` first so unsaved staging is visible
 * to the maths (advisor #4). Offers are PLAUSIBLE roles (type-filtered
 * server-side); "free" is conservative (advisor #1): any sole-held role is a
 * swap — we never assume a role is safe to vacate.
 */
export function metricFixPlan<T extends FixRow>(
  missingRoles: AccountRole[],
  rows: T[],
): MetricFixPlan<T> {
  const holders = new Map<string, number>();
  for (const r of rows) {
    if (r.role && r.consistency !== 'conflict') {
      holders.set(r.role, (holders.get(r.role) ?? 0) + 1);
    }
  }
  const eligible = (role: AccountRole) =>
    rows.filter((r) => r.role !== role && offersOf(r).includes(role));

  const perRole: Array<RoleFixOptions<T>> = missingRoles.map((role) => {
    const usable = eligible(role).filter((r) => r.consistency !== 'conflict');
    return {
      role,
      free: usable.filter((r) => r.role === null || (holders.get(r.role) ?? 0) >= 2),
      swap: usable
        .filter((r) => r.role !== null && (holders.get(r.role) ?? 0) <= 1)
        .map((r) => ({ row: r, vacates: r.role as AccountRole })),
    };
  });

  const covered = flowCover(missingRoles, rows);
  const unfilled: UnfilledRole[] = missingRoles
    .filter((m) => !covered.has(m))
    .map((role) => ({
      role,
      plausibleCount: eligible(role).filter((r) => r.consistency !== 'conflict').length,
    }));

  const allSwaps = perRole.flatMap((p) => p.swap);
  const checkFirst = missingRoles
    .flatMap((role) => eligible(role).filter((r) => r.consistency === 'conflict'))
    .filter((r, i, arr) => arr.findIndex((x) => x.accountId === r.accountId) === i);

  return {
    perRole,
    canUnlockByMapping: unfilled.length === 0,
    unfilled,
    // Prefer naming a swap whose vacated role isn't itself missing (a real
    // trade), else any sole holder — the split guide names a concrete account.
    primarySwap: allSwaps.find((s) => !missingRoles.includes(s.vacates)) ?? allSwaps[0] ?? null,
    checkFirst,
  };
}

/** Overlay unsaved staged answers onto the chart so all fixer maths see them (advisor #4). */
export function applyStagedAnswers<T extends FixRow>(
  rows: T[],
  answers: Record<string, { role: string | null }>,
): T[] {
  return rows.map((r) => (answers[r.accountId] !== undefined ? { ...r, role: answers[r.accountId].role } : r));
}

/**
 * What a save WILL change, computed deterministically from the staged answers
 * (advisor #11 — a stateless checklist shows positions, not movement; this is
 * the movement, toasted on save).
 */
export function stagedCoverageDelta(
  rows: FixRow[],
  answers: Record<string, { role: string | null }>,
): { covered: AccountRole[]; emptied: AccountRole[] } {
  const holders = new Map<string, number>();
  for (const r of rows) {
    if (r.role) holders.set(r.role, (holders.get(r.role) ?? 0) + 1);
  }
  const gains = new Map<string, number>();
  const losses = new Map<string, number>();
  for (const [accountId, a] of Object.entries(answers)) {
    const prior = rows.find((r) => r.accountId === accountId)?.role ?? null;
    if (prior === a.role) continue;
    if (a.role) gains.set(a.role, (gains.get(a.role) ?? 0) + 1);
    if (prior) losses.set(prior, (losses.get(prior) ?? 0) + 1);
  }
  const after = (role: string) =>
    (holders.get(role) ?? 0) + (gains.get(role) ?? 0) - (losses.get(role) ?? 0);

  const touched = new Set([...gains.keys(), ...losses.keys()]);
  const covered: AccountRole[] = [];
  const emptied: AccountRole[] = [];
  for (const role of touched) {
    const before = holders.get(role) ?? 0;
    const now = after(role);
    if (before === 0 && now > 0) covered.push(role as AccountRole);
    if (before > 0 && now === 0) emptied.push(role as AccountRole);
  }
  return { covered, emptied };
}

export interface UnblockStep {
  role: AccountRole;
  /** Ids of the figures this one role is holding back (can't calculate, or only estimated). */
  metricIds: string[];
  /** Their labels, in the same order. */
  metricLabels: string[];
}

/**
 * WHICH QUESTION SWITCHES ON THE MOST FIGURES. One step per missing role, most figures first.
 *
 * Read from each figure's own `dataQuality.missingRoles` — the runtime truth, after the approx
 * overlay — rather than re-deriving from METRIC_REQUIREMENTS, so the ranking can never claim a
 * figure is blocked that the cockpit is showing. A figure missing two roles counts under both:
 * either answer moves it closer, and the header says "questions", not "figures", for that reason.
 * Ties keep METRIC_REQUIREMENTS' role order, so the ranking is stable between renders.
 */
export function unblockRanking(metrics: Pick<Metric, 'id' | 'label' | 'dataQuality'>[]): UnblockStep[] {
  const byRole = new Map<AccountRole, UnblockStep>();
  const seen = new Set<string>();
  for (const m of metrics) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    const dq = m.dataQuality;
    if (dq.state !== 'cannot-compute' && !dq.approximate) continue;
    for (const role of dq.missingRoles ?? []) {
      const step = byRole.get(role) ?? { role, metricIds: [], metricLabels: [] };
      step.metricIds.push(m.id);
      step.metricLabels.push(m.label);
      byRole.set(role, step);
    }
  }
  const order = METRIC_REQUIREMENTS.flatMap((r) => r.roles);
  const rank = (r: AccountRole) => {
    const i = order.indexOf(r);
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...byRole.values()].sort((a, b) => b.metricIds.length - a.metricIds.length || rank(a.role) - rank(b.role));
}

/** How many figures need each role — the static measure of how much answering it switches on. */
const FIGURES_NEEDING: Map<AccountRole, number> = METRIC_REQUIREMENTS.reduce((acc, req) => {
  for (const r of req.roles) acc.set(r, (acc.get(r) ?? 0) + 1);
  return acc;
}, new Map<AccountRole, number>());

/**
 * The one role a fix link on this figure should open: of the roles it is missing, the one the
 * most figures need (Tim, 12 Oct: "most figures is better") — so answering it moves this figure
 * AND as many others as possible. Ties keep the figure's own order. Null when nothing is missing.
 *
 * Static on purpose: every FixIcon calls this without the rest of the model to hand, and the count
 * from METRIC_REQUIREMENTS is the same table the runtime ranking (`unblockRanking`) is built on.
 */
export function fixRoleFor(m: Pick<Metric, 'dataQuality'>): AccountRole | null {
  const dq = m.dataQuality;
  if (dq.state !== 'cannot-compute' && !dq.approximate) return null;
  const missing = dq.missingRoles ?? [];
  if (missing.length === 0) return null;
  return missing.reduce((best, r) => ((FIGURES_NEEDING.get(r) ?? 0) > (FIGURES_NEEDING.get(best) ?? 0) ? r : best));
}
